import { useEffect, useState } from "react";
import { useParams, useNavigate, useSearchParams, Link } from "react-router-dom";
import { GraduationCap, LogIn, UserPlus } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import AppLayout from "@/components/AppLayout";
import Seo from "@/components/Seo";
import { useI18n } from "@/context/I18nContext";
import { langDisplayName } from "@/lib/languages";
import { eduError, PILOT_EXPIRED } from "@/lib/eduErrors";

// Handles both /join/:token (invite link) and /join?code=XXXXXX (class code). Always lands on the class.
export default function JoinClass() {
  const { token } = useParams();
  const [params] = useSearchParams();
  const code = (params.get("code") || "").toUpperCase();
  const navigate = useNavigate();
  const { t, locale } = useI18n();
  const { user, loading: authLoading } = useAuth();
  const [preview, setPreview] = useState(null);
  const [joining, setJoining] = useState(false);
  const [expired, setExpired] = useState(false);
  const [codeInput, setCodeInput] = useState(code);
  const here = token ? `/join/${token}` : `/join${code ? `?code=${code}` : ""}`;
  const open = (cid) => navigate(`/my-classes/${cid}`, { replace: true });

  useEffect(() => {
    if (authLoading || !user || (!token && !code)) return;
    setPreview(null);
    const url = token ? `/join/${token}` : `/classes/join-by-code/${code}`;
    api.get(url).then((r) => {
      if (r.data.already_member) return open(r.data.class_id);
      setPreview(r.data); setExpired(!!r.data.pilot_expired);
    }).catch((e) => setPreview(e?.response?.status === 429 ? "busy" : false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, code, user, authLoading]);

  const join = async () => {
    setJoining(true);
    try {
      const r = token ? await api.post(`/join/${token}`) : await api.post("/classes/join-by-code", { code });
      toast.success(t("edu.cls.joinedToast"));
      open(r.data.class_id);
    } catch (e) {
      if (e?.response?.data?.detail === PILOT_EXPIRED) setExpired(true);
      else toast.error(eduError(e, t, t("edu.cls.joinFailed")));
    } finally { setJoining(false); }
  };

  const enc = encodeURIComponent(here);
  let body;
  if (!authLoading && !user) {
    body = (
      <>
        <p className="text-gray-600 text-sm mt-2">{t("edu.cls.joinAuth")}</p>
        <div className="flex flex-col gap-2 mt-4">
          <Link to={`/login?next=${enc}`} className="nb-btn-primary w-full py-3" data-testid="join-login-btn"><LogIn className="w-4 h-4" /> {t("edu.stu.signIn")}</Link>
          <Link to={`/signup?next=${enc}`} className="nb-btn-outline w-full py-3" data-testid="join-signup-btn"><UserPlus className="w-4 h-4" /> {t("edu.stu.signUp")}</Link>
        </div>
      </>
    );
  } else if (!token && !code) {
    body = (
      <form onSubmit={(e) => { e.preventDefault(); const c = codeInput.trim().toUpperCase().replace(/[^A-Z0-9]/g, ""); if (c.length === 6) navigate(`/join?code=${c}`); }} className="mt-4 space-y-2">
        <input className="nb-input text-center uppercase tracking-widest text-lg" dir="ltr" maxLength={7} value={codeInput} onChange={(e) => setCodeInput(e.target.value)} placeholder="ABC123" data-testid="join-code-input" />
        <button className="nb-btn-primary w-full py-3" data-testid="join-code-submit">{t("edu.stu.continue")}</button>
      </form>
    );
  } else if (preview === false || preview === "busy") {
    body = (
      <>
        <p className="text-coral mt-3" data-testid="join-invalid">{preview === "busy" ? t("edu.stu.tooMany") : token ? t("edu.cls.joinInvalid") : t("edu.stu.codeInvalid")}</p>
        {!token && <Link to="/join" className="underline text-sm mt-2 inline-block" data-testid="join-try-again">{t("edu.stu.tryAgain")}</Link>}
      </>
    );
  } else if (preview) {
    body = (
      <>
        <p className="mt-3 font-bold text-lg" dir="auto" data-testid="join-class-name">{preview.name}</p>
        <p className="text-gray-600 text-sm" data-testid="join-class-school">{langDisplayName(preview.target_language, locale)} · {preview.level} · <bdi>{preview.organization?.name}</bdi></p>
        {expired ? (
          <p className="text-sm mt-4 nb-card bg-peach p-3" data-testid="join-pilot-expired">{t("edu.cls.studentExpired")}</p>
        ) : (
          <button onClick={join} disabled={joining} className="nb-btn-primary w-full py-3 mt-5" data-testid="confirm-join-btn">{joining ? t("edu.cls.joining") : t("edu.cls.joinBtn")}</button>
        )}
      </>
    );
  } else body = <div className="h-24 animate-pulse" />;

  return (
    <AppLayout>
      <Seo title={`${t("edu.cls.joinTitle")} · FluentLoop`} noindex />
      <div className="max-w-md mx-auto px-4 py-12">
        <div className="nb-card p-6 text-center" data-testid="join-card">
          <span className="w-14 h-14 rounded-xl bg-mint border-2 border-ink flex items-center justify-center mx-auto"><GraduationCap className="w-7 h-7" /></span>
          <h1 className="font-cabinet font-extrabold text-2xl mt-4">{t("edu.cls.joinTitle")}</h1>
          {body}
        </div>
      </div>
    </AppLayout>
  );
}
